import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ChatBubbleOutline } from '@mui/icons-material';
import logo from '../../images/logo.png'; // Import your logo image here
import PeopleIcon from '@mui/icons-material/People';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { User } from '../../types';
import {
  checkInChild,
  useFamily,
  useFamilyCheckin,
  useParentUnreadCount,
} from './services';
import { CheckInRecord } from './types';
import ChatWindow from './ChatWindow';
import { printLabels } from './printLabels';
import GuestCheckInWizard from './GuestCheckInWizard';
import LoginPage from '../Members/LoginPage';
import { query, collection, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../firebase';

interface PrintQueueChild {
  id: string;
  childFirstName: string;
  childLastName: string;
  age: number;
  pickupPin: string;
  parentName: string;
}

interface PrintQueueEntry {
  id: string;
  children: PrintQueueChild[];
}

const ParentMessageButton: React.FC<{
  childId: string;
  familyId: string;
  onClick: () => void;
}> = ({ childId, familyId, onClick }) => {
  const unread = useParentUnreadCount(childId, familyId);
  return (
    <IconButton onClick={onClick} color="primary" size="small">
      <Badge badgeContent={unread} color="error">
        <ChatBubbleOutline />
      </Badge>
    </IconButton>
  );
};

const ChildCheckIn: React.FC = () => {
  const navigate = useNavigate();
  const { userData, loading, isKioskMode } = useAuth();
  const family = useFamily(userData?.familyId || '');
  const { checkins } = useFamilyCheckin(userData?.familyId || '');
  const [pendingChild, setPendingChild] = useState<User | null>(null);
  const [msgTarget, setMsgTarget] = useState<CheckInRecord | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [printWarningOpen, setPrintWarningOpen] = useState(false);
  const [showPickupBanner, setShowPickupBanner] = useState(false);
  const [guestWizardOpen, setGuestWizardOpen] = useState(false);
  const [printQueue, setPrintQueue] = useState<PrintQueueEntry[]>([]);

  // Kiosk mode: prevent back navigation and page unload
  useEffect(() => {
    if (!isKioskMode) return;

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isKioskMode]);

  const children = family.filter((member) => member.userType === 'child');
  const activeCheckIns = useMemo(
    () => checkins.filter((r) => !r.checkOutTime),
    [checkins]
  );
  const checkedInIds = useMemo(
    () => new Set(activeCheckIns.map((r) => r.childId)),
    [activeCheckIns]
  );
  const availableChildren = useMemo(
    () => children.filter((c) => !checkedInIds.has(c.userId)),
    [children, checkedInIds]
  );

  const handlePrintClick = async () => {
    if (availableChildren.length > 0) {
      setPrintWarningOpen(true);
    } else {
      const printed = await printLabels(
        activeCheckIns,
        userData?.userId ?? 'parent'
      );
      if (printed) setShowPickupBanner(true);
    }
  };

  const handleConfirmCheckIn = async () => {
    if (!pendingChild || !userData) return;
    setCheckingIn(true);
    try {
      const parentName = `${userData.firstName} ${userData.lastName}`;
      const sessionPin = activeCheckIns[0]?.pickupPin;
      await checkInChild(
        pendingChild,
        userData.userId,
        parentName,
        userData.familyId || '',
        sessionPin
      );
      setPendingChild(null);
    } finally {
      setCheckingIn(false);
    }
  };

  const beginGuestCheckin = () => setGuestWizardOpen(true);

  useEffect(() => {
    if (activeCheckIns.length === 0 || !userData) return;
    const q = query(
      collection(db, 'printQueue'),
      where('familyId', '==', userData.familyId),
      where('status', '==', 'pending')
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const z: PrintQueueEntry[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        z.push({
          id: doc.id,
          ...data,
        } as PrintQueueEntry);
      });
      setPrintQueue(z);
    });
    return () => {
      unsubscribe();
    };
  }, [activeCheckIns, userData]);

  return (
    <Container maxWidth="md" sx={{ mt: isKioskMode ? 1 : 3, mb: 4 }}>
      {isKioskMode && (
        <img
          src={logo}
          alt="Church Logo"
          style={{ display: 'block', margin: '0 auto 20px', maxWidth: 150 }}
        />
      )}
      <Typography
        variant={isKioskMode ? 'h2' : 'h5'}
        textAlign="center"
        fontWeight="bold"
      >
        Children Check-In
      </Typography>

      {!isKioskMode && (
        <>
          {loading && (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              Loading...
            </Typography>
          )}
        </>
      )}
      {isKioskMode && (
        <Stack alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <Button
            variant="contained"
            sx={{ mb: 0, mt: 5, zoom: isKioskMode ? 2 : 1 }}
            size="large"
            onClick={beginGuestCheckin}
          >
            Begin Check-In
          </Button>
          <Typography
            variant={isKioskMode ? 'h5' : 'body1'}
            color="text.secondary"
            sx={{ p: 2 }}
          >
            You can also scan the QR Code below with your phone, or go to{' '}
            <b>www.nmzlakeland.org/checkin</b> on your phone to check-in your
            children
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0 }}>
            <img
              src={require('./checkin_qr.png')}
              alt="Check-In QR Code"
              style={{ width: '100%', maxWidth: 200, marginTop: 0 }}
            />
          </Box>
        </Stack>
      )}

      {!userData && !loading && (
        <div>
          <Typography
            variant="body1"
            color="error"
            textAlign="center"
            sx={{ mb: 2, mt: 5 }}
          >
            Login in or create an account to check-in your child.
          </Typography>

          <LoginPage onUserSet={() => {}} />
        </div>
      )}

      {!isKioskMode && (
        <>
          {activeCheckIns.length > 0 && (
            <div style={{ marginTop: 30 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">
                  Currently Checked-In ({activeCheckIns.length})
                </Typography>
                {/* <Badge badgeContent={printQueue.length} color="warning"> */}
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintClick}
                >
                  Print Name Tags
                </Button>
                {/* </Badge> */}
              </Stack>
              {printQueue.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Alert
                    severity="info"
                    sx={{
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  >
                    Printing {printQueue.flatMap((x) => x.children).length} name
                    tag(s) ...
                  </Alert>
                  <LinearProgress />
                </Box>
              )}
              {showPickupBanner && (
                <Alert
                  severity="success"
                  onClose={() => setShowPickupBanner(false)}
                  sx={{ mb: 1 }}
                >
                  Name tag(s) sent to printer — please pick up from the front
                  desk.
                </Alert>
              )}
              {activeCheckIns.map((checkin) => (
                <Box
                  key={checkin.id}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1,
                    mb: 2,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      src={checkin.photoUrl ?? undefined}
                      alt={checkin.childFirstName}
                      sx={{ width: 56, height: 56 }}
                    >
                      {checkin.childFirstName?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {checkin.childFirstName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Checked in at:{' '}
                        {checkin.checkInTime.toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pincode: {checkin.pickupPin}
                      </Typography>
                    </Box>
                    {!checkin.isWalkIn && (
                      <ParentMessageButton
                        childId={checkin.childId}
                        familyId={checkin.familyId}
                        onClick={() => setMsgTarget(checkin)}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
              <Divider sx={{ my: 3 }} />
            </div>
          )}

          {userData && !loading && availableChildren.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                Available for Check-In ({availableChildren.length})
              </Typography>
              {availableChildren.map((child) => (
                <Box
                  key={child.userId}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1,
                    mb: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        src={child.profilePic ?? undefined}
                        alt={child.firstName}
                        sx={{ width: 56, height: 56 }}
                      >
                        {child.firstName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {child.firstName} {child.lastName}
                        </Typography>
                        {child.allergies && child.allergies !== 'N/A' && (
                          <Typography variant="body2" color="error">
                            ⚠ {child.allergies}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setPendingChild(child)}
                    >
                      Check In
                    </Button>
                  </Stack>
                </Box>
              ))}
            </>
          )}

          {userData && children.length === 0 && (
            <Typography
              variant="h5"
              color="text.secondary"
              textAlign="center"
              p={5}
            >
              No children added yet
            </Typography>
          )}
          {userData && !loading && (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/checkin/family')}
              >
                Add or Edit Children
              </Button>
            </>
          )}
        </>
      )}

      <GuestCheckInWizard
        open={guestWizardOpen}
        onClose={() => setGuestWizardOpen(false)}
      />

      {msgTarget && (
        <ChatWindow
          open={!!msgTarget}
          onClose={() => setMsgTarget(null)}
          childId={msgTarget.childId}
          familyId={msgTarget.familyId}
          childName={msgTarget.childFirstName}
          role="parent"
          senderId={userData?.userId ?? ''}
          senderName={`${userData?.firstName ?? ''} ${
            userData?.lastName ?? ''
          }`}
        />
      )}

      <Dialog
        open={printWarningOpen}
        onClose={() => setPrintWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Not All Children Checked In</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {availableChildren.map((c) => c.firstName).join(', ')}{' '}
            {availableChildren.length === 1 ? 'has' : 'have'} not been checked
            in yet.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Do you still want to print name tags for the children who are
            checked in?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintWarningOpen(false)}>Go Back</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setPrintWarningOpen(false);
              const printed = await printLabels(
                activeCheckIns,
                userData?.userId ?? 'parent'
              );
              if (printed) setShowPickupBanner(true);
            }}
          >
            Print Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingChild}
        onClose={() => !checkingIn && setPendingChild(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Check-In</DialogTitle>
        <DialogContent>
          {pendingChild && (
            <Stack alignItems="center" spacing={1} sx={{ pt: 1 }}>
              <Avatar
                src={pendingChild.profilePic ?? undefined}
                alt={pendingChild.firstName}
                sx={{ width: 80, height: 80, fontSize: 32 }}
              >
                {pendingChild.firstName?.[0]}
              </Avatar>
              <Typography variant="h6">{pendingChild.firstName}</Typography>
              <Typography variant="body2" color="text.secondary">
                After checking in all your children, print their name tags,
                pickup the tags from the front desk kiosk, and attach them to
                your child.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingChild(null)} disabled={checkingIn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCheckIn}
            disabled={checkingIn}
            startIcon={checkingIn ? <CircularProgress size={16} /> : undefined}
          >
            {checkingIn ? 'Checking In…' : 'Confirm Check-In'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ChildCheckIn;
